/**
 * Bounded-query helpers for the admin panel (issue #358).
 *
 * Every list view used to call `getDocs()` on a whole collection and then
 * filter, search and paginate the result in the browser. That works on a
 * demo dataset and degrades without ever raising an error: cost and latency
 * grow linearly with the collection, and the first symptom is a bill or a
 * hung tab, not a stack trace.
 *
 * These helpers keep the page-size arithmetic in one place so the three list
 * views cannot drift, and so the awkward parts — a junk page size, the
 * over-fetch used to detect a next page, an export loop that must terminate —
 * are unit-testable without Firestore.
 */

/** Rows fetched per page when the caller does not ask for a specific size. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Upper bound on a single page. A caller asking for more is clamped rather
 * than refused: the point is that no single query can be unbounded, not that
 * the caller must guess the limit correctly.
 */
export const MAX_PAGE_SIZE = 100;

/**
 * Hard stop for the paged export loop. An export is a deliberate, user-initiated
 * action, so it is allowed to read far more than a page — but it still must not
 * be able to run forever against a collection that is being written to while it
 * reads.
 */
export const MAX_EXPORT_ROWS = 10_000;

/** One page of results plus what is needed to ask for the next one. */
export interface Page<T> {
  rows: T[];
  /** Opaque cursor for the next page, or `null` when this is the last one. */
  cursor: unknown | null;
  hasMore: boolean;
}

/**
 * Coerce a caller-supplied page size into a usable one.
 *
 * Returns DEFAULT_PAGE_SIZE for anything that is not a positive whole number —
 * `undefined`, `null`, `NaN`, `Infinity`, `0`, negatives, decimals and values
 * that arrived as strings from a query parameter. The alternative, passing a
 * junk value through to `limit()`, throws inside Firestore with a message that
 * means nothing to an operator.
 */
export function normalisePageSize(value?: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_PAGE_SIZE;
  }
  if (!Number.isInteger(value) || value < 1) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(value, MAX_PAGE_SIZE);
}

/**
 * Split an over-fetched result into a page.
 *
 * Detecting "is there a next page" by counting the returned documents is
 * ambiguous — a collection whose size is an exact multiple of the page size
 * looks the same as one that has ended. Asking for `pageSize + 1` and
 * discarding the extra removes the ambiguity for one extra document read.
 */
export function splitOverfetch<T>(docs: T[], pageSize: number): { rows: T[]; hasMore: boolean } {
  const size = normalisePageSize(pageSize);
  if (docs.length > size) {
    return { rows: docs.slice(0, size), hasMore: true };
  }
  return { rows: docs, hasMore: false };
}

/** The limit to pass to Firestore for a page of `pageSize` rows. */
export function overfetchLimit(pageSize: number): number {
  return normalisePageSize(pageSize) + 1;
}

/**
 * Was an export truncated by the safety stop rather than by running out of data?
 *
 * Exposed separately so the caller can tell the operator the file is partial.
 * Silently writing a short CSV is the failure mode worth avoiding here — it
 * looks like a complete export of a smaller business.
 */
export function exportWasTruncated(rowCount: number, hasMore: boolean): boolean {
  return hasMore && rowCount >= MAX_EXPORT_ROWS;
}

/**
 * How many rows the next export page may fetch without crossing MAX_EXPORT_ROWS.
 * Returns 0 once the cap is reached, which is the loop's termination condition.
 */
export function remainingExportBudget(rowsSoFar: number): number {
  const remaining = MAX_EXPORT_ROWS - rowsSoFar;
  if (remaining <= 0) return 0;
  return Math.min(remaining, MAX_PAGE_SIZE);
}
